import { Routes as Router, Route } from 'react-router-dom'
import AutoAuthRoutes from './AutoAuthRoutes'
import SignUp from '../pages/User/SignUp'
import ForgotPage from '../pages/User/Forgot'
import ChangePasswordPage from '../pages/User/ChangePassword'
import UnauthorizedPage from '../pages/Unauthorized'
import AdminProtected from './ProtectedRoutes/AdminProtected'
import DashboardPage from '../pages/Dashboard'
import ProductsPage from '../pages/Products'
import ProductPage from '../pages/Products/Product'
import AppsPage from '../pages/Apps'
import CheckoutOrdersPage from '../pages/Checkouts/Orders'
import CheckoutOpenPage from '../pages/Checkouts/Open'
import CheckoutAbandonedPage from '../pages/Checkouts/Abandoned'
import CheckoutSubscriptionPage from '../pages/Checkouts/Subscription'
import SysAdminProtected from './ProtectedRoutes/SysAdminProtected'
import ProductAffiliateRecruiting from '../pages/Products/AffiliateRecruiting'
import AffiliateRecruitingAffiliationPage from '../pages/Products/AffiliateRecruiting/AffiliateRecruitingAffiliation'
import ProductAcceptForm from '../pages/Products/ProductAcceptForm'
import { AdminUserProvider } from '../core/context/AdminUserContext'
import React, { Suspense } from 'react'
import ProgressIndicatorPageCenter from '../components/Progress/ProgressIndicatorPageCenter'
import { SalesTeamProvider } from '../core/context/SalesTeamContext'
import DisputeAccessGate from '../components/Disputes/DisputeAccessGate'

// Lazy load - low-traffic and admin pages
// This improves the initial load time of the app by only loading these components when they are needed
const AffiliatedsPage = React.lazy(
  () =>
    import('../pages/Affiliateds').then((m) => ({ default: m.AffiliatedsPage }))
)
const CommissionRulesPage = React.lazy(() => import('../pages/CommissionRules'))
const CademiPage = React.lazy(
  () => import('../pages/Apps/Cademi/CademiListing')
)
const WebhooksPage = React.lazy(() => import('../pages/Webhooks/List'))
const MarketplacePage = React.lazy(() => import('../pages/Marketplace'))
const WebhookSendsPage = React.lazy(() => import('../pages/Webhooks/Sends'))
const DisputesPage = React.lazy(() => import('../pages/Disputes'))
const DisputeDetailPage = React.lazy(() => import('../pages/Disputes/Detail'))
const SettingsPage = React.lazy(
  () =>
    import('../pages/settings').then((m) => ({ default: m.SettingsPage }))
)
const IndicatedPage = React.lazy(() => import('../pages/Indicated'))
const CustomCheckoutsPage = React.lazy(
  () => import('../pages/Customs/Checkout/CustomCheckouts')
)
const SalesFunnelPage = React.lazy(() => import('../pages/Sales/Funnel'))
const SalesFunnelNewEditPage = React.lazy(
  () => import('../pages/Sales/Funnel/SalesFunnelNewEdit')
)
const SalesFunnelStepsPage = React.lazy(
  () => import('../pages/Sales/Funnel/SalesFunnelSteps')
)
const SalesTeamPage = React.lazy(() => import('../pages/Sales/Team'))
const ActiveCampaignPage = React.lazy(
  () => import('../pages/Apps/ActiveCampaign')
)
const ActiveCampaignSmartPage = React.lazy(
  () => import('../pages/Apps/ActiveCampaign/ActiveCampaignSmart')
)
const TheMembersPage = React.lazy(
  () => import('../pages/Apps/TheMembers/TheMembersListing')
)
const AstronMembersPage = React.lazy(
  () => import('../pages/Apps/AstronMembers/AstronMembersListing')
)
const NotazzPage = React.lazy(() => import('../pages/Apps/Notazz'))
const UtmfyList = React.lazy(() => import('../pages/Apps/Utmfy/UtmfyList'))
const SellFluxPage = React.lazy(
  () => import('../pages/Apps/SellFlux/SellFluxListing')
)
const MemberKitPage = React.lazy(
  () => import('../pages/Apps/MemberKit/MemberKitListing')
)
const UserSettingsPage = React.lazy(
  () => import('../pages/Admin/UserSettings')
)
const SecurityDashboardPage = React.lazy(
  () => import('../pages/Admin/SecurityDashboard')
)
const CheckoutAccessPage = React.lazy(
  () => import('../pages/Admin/Checkouts/Access')
)
const CheckoutOrderAnalitycsPage = React.lazy(
  () => import('../pages/Admin/Checkouts/Analytics')
)
const CheckoutOrderUsersAnticipationPage = React.lazy(
  () => import('../pages/Admin/Checkouts/Anticipations')
)
const OccurrenceLogPage = React.lazy(
  () => import('../pages/Admin/Checkouts/OccurrenceLog')
)
const SalesAnalysis = React.lazy(
  () => import('../pages/Admin/Checkouts/SalesAnalysis')
)
const DivergentBalancePage = React.lazy(
  () => import('../pages/Admin/DivergentBalance')
)
const AuditLogPage = React.lazy(() => import('../pages/Admin/AuditLog'))

type Props = {}

const Routes = (props: Props) => {
  return (
    <Suspense fallback={<ProgressIndicatorPageCenter />}>
      <Router>
        <Route index element={<AutoAuthRoutes />} />
        <Route path="/signup" element={<SignUp />} />

        <Route path="/forgot" element={<ForgotPage />} />
        <Route
          path="/:codeId/changepassword"
          element={<ChangePasswordPage />}
        />

        <Route path="/unauthorized" element={<UnauthorizedPage />} />

        <Route
          path="/affiliate-recruiting/view/:codeId"
          element={<ProductAffiliateRecruiting />}
        />

        <Route element={<AdminProtected />}>
          <Route path="/dashboard" element={<DashboardPage />} />

          <Route
            path="/products"
            element={
              <AdminUserProvider>
                <ProductsPage />
              </AdminUserProvider>
            }
          />
          <Route path="/products/form" element={<ProductAcceptForm />} />
          <Route path="/product/:id" element={<ProductPage />} />
          <Route
            path="/product/affiliation/:id"
            element={<AffiliateRecruitingAffiliationPage />}
          />

          <Route path="/affiliateds" element={<AffiliatedsPage />} />
          <Route path="/commissionrules" element={<CommissionRulesPage />} />

          <Route path="/apps" element={<AppsPage />} />

          <Route path="/apps/cademi" element={<CademiPage />} />
          <Route path="/apps/utmfy" element={<UtmfyList />} />
          <Route path="/apps/themembers" element={<TheMembersPage />} />
          <Route path="/apps/astronmembers" element={<AstronMembersPage />} />
          <Route path="/apps/webhooks" element={<WebhooksPage />} />
          <Route path="/apps/activecampaign" element={<ActiveCampaignPage />} />
          <Route
            path="/apps/activecampaign/:activeCampaignId/:id"
            element={<ActiveCampaignSmartPage />}
          />
          <Route path="/apps/notazz" element={<NotazzPage />} />
          <Route path="/apps/memberkit" element={<MemberKitPage />} />
          <Route path="/apps/sellflux" element={<SellFluxPage />} />

          <Route path="/sales/funnel" element={<SalesFunnelPage />} />
          <Route
            path="/sales/funnel/:id"
            element={<SalesFunnelNewEditPage />}
          />
          <Route
            path="/sales/funnel/:id/steps"
            element={<SalesFunnelStepsPage />}
          />

          <Route
            path="/sales/team"
            element={
              <SalesTeamProvider>
                <SalesTeamPage />
              </SalesTeamProvider>
            }
          />

          <Route path="/marketplace" element={<MarketplacePage />} />

          <Route path="/checkout/order" element={<CheckoutOrdersPage />} />
          <Route path="/checkout/open" element={<CheckoutOpenPage />} />
          <Route
            path="/checkout/abandoned"
            element={<CheckoutAbandonedPage />}
          />
          <Route
            path="/checkout/subscription"
            element={<CheckoutSubscriptionPage />}
          />

          <Route path="/checkout/customs" element={<CustomCheckoutsPage />} />

          <Route path="/webhooks/sends" element={<WebhookSendsPage />} />

          <Route
            path="/disputes"
            element={
              <DisputeAccessGate>
                <DisputesPage />
              </DisputeAccessGate>
            }
          />
          <Route
            path="/disputes/:id"
            element={
              <DisputeAccessGate>
                <DisputeDetailPage />
              </DisputeAccessGate>
            }
          />

          <Route path="/settings" element={<SettingsPage />} />

          <Route path="/indicated" element={<IndicatedPage />} />

          <Route element={<SysAdminProtected />}>
            <Route
              path="/checkout/analytics"
              element={<CheckoutOrderAnalitycsPage />}
            />
            <Route path="/checkout/access" element={<CheckoutAccessPage />} />
            <Route path="/checkout/anticipations" element={<CheckoutOrderUsersAnticipationPage />}
            />
            <Route path="/admin/occurrences" element={<OccurrenceLogPage />} />

            <Route path="/admin/sales/analysis" element={<SalesAnalysis />} />

            <Route
              path="/settings/users"
              element={
                <AdminUserProvider>
                  <UserSettingsPage />
                </AdminUserProvider>
              }
            />

            <Route
              path="/balance/divergents"
              element={<DivergentBalancePage />}
            />

            <Route path="/admin/audit-log" element={<AuditLogPage />} />
            <Route
              path="/admin/security-dashboard"
              element={<SecurityDashboardPage />}
            />
          </Route>
        </Route>
      </Router>
    </Suspense>
  )
}

export default Routes
